import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";

const COUNTRIES = [
  ["US", "United States"], ["GB", "United Kingdom"], ["IE", "Ireland"], ["CA", "Canada"], ["AU", "Australia"],
  ["NZ", "New Zealand"], ["SG", "Singapore"], ["ZA", "South Africa"], ["DE", "Germany"], ["NL", "Netherlands"],
  ["FR", "France"], ["ES", "Spain"], ["PT", "Portugal"], ["IT", "Italy"], ["PL", "Poland"], ["SE", "Sweden"],
  ["DK", "Denmark"], ["NO", "Norway"], ["FI", "Finland"], ["CH", "Switzerland"], ["AT", "Austria"], ["BE", "Belgium"],
  ["CZ", "Czechia"], ["RO", "Romania"], ["EE", "Estonia"],
];

export const WorkAuthDialog = ({ open, onOpenChange }) => {
  const { user, checkAuth } = useAuth();
  const [selected, setSelected] = useState(user?.work_authorizations || []);
  const [saving, setSaving] = useState(false);

  const toggle = (code) => setSelected(s => s.includes(code) ? s.filter(c => c !== code) : [...s, code].slice(0, 12));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/work-authorizations", { countries: selected });
      await checkAuth?.();
      toast.success(selected.length ? "Saved — roles in those countries will now be shown" : "Cleared — only cross-border remote roles will be shown");
      onOpenChange(false);
    } catch { toast.error("Could not save your work authorisation"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[88dvh] overflow-y-auto p-4 sm:p-6" data-testid="work-auth-dialog">
        <DialogHeader>
          <DialogTitle>Where can you legally work?</DialogTitle>
          <DialogDescription className="text-slate-500">
            By default we only show overseas roles where the posting states it hires across borders. Tick any country where you already hold a valid visa or work permit and we'll include its local roles too.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {COUNTRIES.map(([code, name]) => (
            <button key={code} onClick={() => toggle(code)} data-testid={`work-auth-${code}`}
              className={`rounded-full border px-3 py-2 text-xs transition-colors ${selected.includes(code) ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-300 text-slate-500 hover:border-slate-300"}`}>
              {name}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500">We never verify documents — this only controls which jobs you're shown. India is always included.</p>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <Button onClick={save} disabled={saving} className="h-11 bg-sky-600 hover:bg-sky-500 text-white" data-testid="work-auth-save">{saving ? "Saving…" : "Save"}</Button>
          <Button onClick={() => setSelected([])} variant="outline" className="h-11 border-slate-300 text-slate-700" data-testid="work-auth-clear">Clear all</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkAuthDialog;
